<template>
    <div class="flex h-screen w-full max-w-screen">
        <!-- Sidebar: hidden on mobile for field staff (CSS handles responsiveness) -->
        <aside
            v-if="showLayout"
            class="relative border-r print:hidden"
            :class="[appStore.sideMini ? 'lg:w-16' : 'lg:w-64', isRestrictedFieldStaff ? 'hidden lg:block' : '']"
        >
            <button
                type="button"
                class="absolute top-7 -right-4 z-50 hidden h-7 lg:block"
                @click="appStore.sideMini = !appStore.sideMini"
            >
                <div class="w-fit rounded-full border bg-white p-0.5 shadow-sm">
                    <SvgIcon
                        name="system-chevron-left"
                        class="text-muval-gray-2 size-5 transition"
                        :class="appStore.sideMini && 'rotate-180'"
                    />
                </div>
            </button>
            <NavigationController />
        </aside>
        <!--
            The queue footer's reservation lands here, on the column that holds the
            header and the scroll container, rather than on the container itself.
            Padding here reduces the space `main` can grow into, so `main`'s box
            genuinely shrinks — which is what every `h-full` chain inside a
            `fullHeight` view resolves against. Reserving on `main` instead only
            shrank its content box, and an intrinsic-height card (the leads and jobs
            tables) simply overflowed it and kept its last row under the bar.
        -->
        <div
            class="relative flex w-0 flex-1 flex-col"
            :style="footerReservation"
        >
            <NewTopHeader
                v-if="showLayout && !isRestrictedFieldStaff"
                class="z-40 -mb-1 bg-white px-4 pb-4 transition-shadow lg:px-8 print:hidden"
                :class="[
                    fullWidthView ? 'max-w-full' : 'max-w-[1920px]',
                    !arrivedState.top ? 'shadow-xs' : 'shadow-none',
                ]"
            />
            <!--
                The queue footer is `fixed`, so it overlays rather than pushes:
                without this the last row of every scrollable page in the CRM sits
                underneath it for as long as a queue is open. The height reserved on
                the column above is the one the footer measured of itself, so the
                reservation cannot drift from what it actually occupies — including
                when the bar wraps onto extra lines at mobile widths.
            -->
            <main
                id="layout-main-scroll-container"
                ref="mainSection"
                class="flex-1 overflow-auto pt-4"
                :class="isRestrictedFieldStaff ? 'field-staff-main' : 'h-[calc(100vh-60px)]'"
            >
                <div
                    class="h-full lg:px-8"
                    :class="fullWidthView ? 'max-w-full' : 'max-w-[1920px]'"
                >
                    <div
                        :class="{
                            'h-full': fullHeightView,
                            'pb-4 lg:pb-8': !fullHeightView,
                        }"
                    >
                        <slot />
                    </div>
                </div>
            </main>
        </div>
        <NotificationSlideOver
            v-if="userStore.isAuthenticated"
            class="z-40 print:hidden"
        />
        <NotificationSlideOver
            v-if="userStore.isAuthenticated"
            scope="visitor"
            class="z-40 print:hidden"
        />
        <ThemeManager />
        <!-- Mobile bottom bar for field staff -->
        <MobileBottomBar
            v-if="showLayout && isRestrictedFieldStaff"
            class="lg:hidden print:hidden"
        />
        <!--
            The sales agent's working queue. Mounted unconditionally alongside the
            field-staff bar rather than behind a queue check, because it is the
            component that owns the active-queue query — it renders nothing until
            there is one, and it has to be watching before a queue is built for the
            first advance to take the page with it.
        -->
        <SalesQueueFooter v-if="showLayout" />
    </div>
</template>
<script setup>
import SalesQueueFooter from '@components/sales/SalesQueueFooter.vue';
import { salesQueueFooterHeight } from '@composables/useSalesQueue';

const userStore = useUserStore();
const route = useRoute();
const appStore = useAppStore();

const showLayout = computed(() => {
    return (
        userStore.isAuthenticated &&
        route.name !== 'Login' &&
        route.name !== 'Register' &&
        route.name !== 'Update your preferences'
    );
});

const fullWidthView = computed(() => {
    return route.meta?.fullWidth;
});

const fullHeightView = computed(() => {
    return route.meta?.fullHeight;
});

// Field staff get bottom bar on mobile (CSS handles the breakpoint)
const isRestrictedFieldStaff = computed(() => userStore.isRestrictedFieldStaff?.());

const mainSection = useTemplateRef('mainSection');
const { arrivedState } = useScroll(mainSection);

/**
 * Zero unless a queue is running, in which case the footer publishes its own
 * measured height and this reserves it — as bottom padding on the column that
 * holds the scroll container, for the reason given on that element. Padding rather
 * than a height because `main` is a `flex-1` item whose used height comes from flex
 * growth, so a `height` of our own is ignored outright; and measured rather than a
 * `100vh` calc because the header is 76px, not the 60px the old calc guessed.
 * Field staff are left alone: their pane is governed by `.field-staff-main` and
 * they hold no queue.
 */
const footerReservation = computed(() => {
    if (isRestrictedFieldStaff.value || salesQueueFooterHeight.value === 0) {
        return undefined;
    }

    return { paddingBottom: `${salesQueueFooterHeight.value}px` };
});
</script>

<style scoped>
.field-staff-main {
    max-height: calc(100dvh - 3.5rem - env(safe-area-inset-bottom, 0px));
}

@media (min-width: 1024px) {
    .field-staff-main {
        max-height: none;
    }
}
</style>
